type FetcherFunction<FetchOptions extends Record<string, any> = Record<string, any>, ResponseType = any> = (
  opts: FetchOptions,
) => Promise<ResponseType>;
type KeyGenerator<F extends Record<string, any>, K extends string = string> = (opts: F) => K;
type ResponseTransformer<C, R, F> = (data: R, opts: F) => C;
type Listener<C> = (data: C) => void;
type ListenerMap<K extends string, C> = Record<K, Array<Listener<C>>>;
type InitialPop<C, K extends string> = (setter: (key: K, value: C) => void) => void;
type Prefetcher<K, C> = () => Promise<readonly (readonly [K, C])[]>;

// Bit of an overkill perhaps
type ResolveReturnType<
  O,
  OptionsKeyMap extends Partial<Record<string, any>>,
  KM extends Record<string, any>,
  Default,
> = {
  [K in keyof OptionsKeyMap]: O extends OptionsKeyMap[K] ? (K extends keyof KM ? KM[K] : Default) : never;
}[keyof OptionsKeyMap] extends never
  ? Default
  : {
      [K in keyof OptionsKeyMap]: O extends OptionsKeyMap[K] ? (K extends keyof KM ? KM[K] : Default) : never;
    }[keyof OptionsKeyMap];

export function createApiCache<
  FetchOptions extends Record<string, any>,
  ResponseType,
  CacheType = ResponseType,
  CacheKey extends string = string,
  KeyMap extends Record<CacheKey, any> = Record<CacheKey, CacheType>,
  OptionsKeyMap extends Partial<Record<CacheKey, FetchOptions>> = never,
>(
  name: string,
  fetcher: FetcherFunction<FetchOptions, ResponseType>,
  keyGen: KeyGenerator<FetchOptions, CacheKey>,
  transformer: ResponseTransformer<CacheType, ResponseType, FetchOptions>,
  populate?: InitialPop<CacheType, CacheKey>,
  prefetch?: Prefetcher<CacheKey, CacheType>,
) {
  const dataCache = new Map<CacheKey, CacheType>();
  const promiseCache = new Map<CacheKey, Promise<ResponseType>>();

  let prefetchPromise: Promise<readonly (readonly [CacheKey, CacheType])[]> | undefined;
  let prefetchDone = false;

  const listeners = {} as ListenerMap<CacheKey, CacheType>;

  populate?.((key, value) => dataCache.set(key, value));

  const doPrefetch = async () => {
    if (dataCache.size === 0 && prefetch && !prefetchDone) {
      prefetchPromise ??= prefetch();
      const allData = await prefetchPromise;

      for (const [key, value] of allData) {
        dataCache.set(key, value);
      }
      prefetchDone = true;
    }
  };

  async function fetchData<O extends FetchOptions>(
    opts: O,
    forceRefresh = false,
  ): Promise<[OptionsKeyMap] extends [never] ? CacheType : ResolveReturnType<O, OptionsKeyMap, KeyMap, CacheType>> {
    await doPrefetch();

    const key = keyGen(opts);

    if (!forceRefresh && dataCache.has(key)) {
      return dataCache.get(key)! as [OptionsKeyMap] extends [never]
        ? CacheType
        : ResolveReturnType<O, OptionsKeyMap, KeyMap, CacheType>;
    }

    const fetchPromise = promiseCache.has(key)
      ? promiseCache.get(key)!
      : (async () => {
          try {
            const response = await fetcher(opts);

            if (!response) {
              throw new Error(`Did not receive data for cache ${name}!`);
            }

            return response;
          } catch (err) {
            promiseCache.delete(key);
            throw err;
          }
        })();

    promiseCache.set(key, fetchPromise);

    const data = await fetchPromise;

    promiseCache.delete(key);

    const transformed = transformer(data, opts);

    dataCache.set(key, transformed);

    if (listeners[key]) {
      for (const fn of listeners[key]) {
        fn(transformed);
      }
    }

    return transformed as [OptionsKeyMap] extends [never]
      ? CacheType
      : ResolveReturnType<O, OptionsKeyMap, KeyMap, CacheType>;
  }

  function clear() {
    dataCache.clear();
    promiseCache.clear();
  }

  function get<K extends CacheKey>(key: K): KeyMap[K] | undefined {
    return dataCache.get(key) as KeyMap[K] | undefined;
  }

  function has(key: CacheKey) {
    return dataCache.has(key);
  }

  async function all(asArray: true): Promise<CacheType[]>;
  async function all(asArray?: false): Promise<Set<CacheType>>;
  async function all(asArray = false) {
    await doPrefetch();

    if (asArray) return Array.from(dataCache.values());

    return new Set(dataCache.values());
  }

  function size() {
    return dataCache.size;
  }

  function addListener(key: CacheKey, fn: Listener<CacheType>) {
    if (!listeners[key]) {
      listeners[key] = [];
    }

    if (listeners[key].includes(fn)) {
      return;
    }
    listeners[key].push(fn);

    if (dataCache.has(key)) {
      fn(dataCache.get(key)!);
    }
  }

  function removeListener(key: CacheKey, fn: Listener<CacheType>) {
    if (!listeners[key]) {
      return;
    }

    listeners[key] = listeners[key].filter((v) => v !== fn);
  }

  return {
    fetchData,
    clear,
    get,
    has,
    all,
    size,
    addListener,
    removeListener,
  };
}
